import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

// @ts-ignore

import Dropdown from '@app/components/ui/Dropdown';
import { SelectOption } from '@app/components/ui/Select';
import { cn, parseNumber } from '@app/libs/utils';
import readNumber from 'read-vn-number';

import WarningIcon from '../icons/WarningIcon';

import Tooltip from './Tooltip';

// Format number with spaces as thousand separators for display
function formatNumberWithSpaces(value: string, noGroup: boolean = false): string {
  if (noGroup) return value;
  if (value == null) return '';
  // Do not format if value is just a dot or empty
  if (value === '' || value === '.' || value === ',') return value;
  // Normalize comma to dot for decimal
  const normalized = value.replace(/,/g, '.');
  const hasDot = normalized.includes('.');
  const [intPart, decPart] = normalized.split('.');
  // Remove existing spaces in int part then add spaces every 3 digits
  const cleanInt = intPart.replace(/\s/g, '');
  // Handle leading zeros carefully (keep a single leading zero when appropriate)
  const formattedInt = cleanInt.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return hasDot ? `${formattedInt}${decPart !== undefined ? `.${decPart}` : ''}` : formattedInt;
}

const FUNCTION_KEYS = [
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'Tab',
  'Enter',
  'ArrowUp',
  'ArrowDown',
  'Tab',
];

interface InputProps {
  label?: string;
  description?: string;
  placeholder?: string;
  value?: string | number;
  onChange?: (value: string) => void;
  units?: string | SelectOption[];
  unitValue?: string;
  onUnitChange?: (value: string) => void;
  type?: 'text' | 'number' | 'email' | 'tel';
  required?: boolean;
  predict?: boolean;
  showTextNumber?: boolean;
  quotient?: boolean;
  maxLength?: number;
  className?: string;
  containerClassName?: string;
  children?: React.ReactNode;
  id?: string;
  errors?: string[];
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  onEnter?: (...args: any[]) => void;
  integer?: boolean;
  validate?: (value?: string) => any;
  isDirty?: boolean;
  onDirty?: (isDirty: boolean) => void;
  noGroup?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label = '',
      description,
      placeholder,
      value,
      onChange,
      units,
      unitValue,
      onUnitChange,
      type = 'text',
      required,
      showTextNumber,
      quotient,
      predict,
      maxLength,
      className,
      containerClassName,
      children,
      id,
      errors,
      disabled,
      onEnter,
      integer = false,
      validate,
      isDirty: dirty = false,
      onDirty,
      min,
      max,
      noGroup = false,
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(ref as any);
    const [currentValue, setCurrentValue] = useState<string>('');
    const initialValue = useRef<boolean>(true);
    const [isDirty, setIsDirty] = useState<boolean>(false);

    useEffect(() => {
      setIsDirty(dirty);
    }, [dirty]);

    useEffect(() => {
      onDirty?.(isDirty);
    }, [isDirty]);

    const isValid = useMemo<boolean>(() => {
      if (!isDirty) return true;
      if (!validate) return true;
      if (!currentValue && !required) return true;
      const error = validate(currentValue?.toString());
      return error === true;
    }, [currentValue, isDirty]);

    useEffect(() => {
      if (value === undefined || value === null || value === '') {
        setCurrentValue('');
        return;
      }

      if (type === 'number') {
        if (integer) {
          // Lưu giá trị thô (không khoảng trắng) cho state, hiển thị sẽ format sau
          setCurrentValue(parseNumber(value.toString()).toString());
        } else {
          // Số thập phân: chuẩn hóa về dấu chấm và chỉ một dấu chấm, không chứa khoảng trắng
          let valueStr = value.toString().replace(/\s/g, '');
          valueStr = valueStr.replace(/,/g, '.');
          const parts = valueStr.split('.');
          if (parts.length > 2) {
            valueStr = parts[0] + '.' + parts.slice(1).join('');
          }
          setCurrentValue(valueStr);
        }
        return;
      }

      setCurrentValue(prev => (prev === value ? prev : value.toString()));
    }, [value, type, integer]);

    const handleInput = useCallback(
      (e: KeyboardEvent) => {
        const value = currentValue?.toString() || '';

        if (e.key === 'Enter' && onEnter) {
          onEnter((e.target as HTMLInputElement)?.value as string);
          return;
        }

        if (
          maxLength &&
          value?.length > maxLength &&
          e.key !== 'Backspace' &&
          e.key !== 'Delete' &&
          e.key !== 'ArrowLeft' &&
          e.key !== 'ArrowRight' &&
          e.key !== 'Tab' &&
          e.key !== 'Enter'
        ) {
          e.preventDefault();
          return;
        }

        // Chặn nhập số sau "0" chỉ khi không phải số thập phân (integer = true)
        // Nếu là số thập phân (integer = false), cho phép nhập dấu chấm/phẩy sau "0"
        if (type === 'number' && integer && value === '0' && /[\d]/.test(e.key)) {
          e.preventDefault();
          return;
        }

        if (type === 'number' && e.key === 'e') {
          e.preventDefault();
          return;
        }

        // Cho phép nhập dấu chấm cho số thập phân nếu không phải integer
        if (type === 'number' && !integer && e.key === '.') {
          // Chỉ cho phép một dấu chấm
          if (value.includes('.')) {
            e.preventDefault();
            return;
          }
          // Cho phép nhập dấu chấm
          return;
        }

        // Cho phép nhập dấu phẩy cho số thập phân nếu không phải integer (convert thành dấu chấm)
        if (type === 'number' && !integer && e.key === ',') {
          // Chỉ cho phép một dấu chấm (convert dấu phẩy thành dấu chấm)
          if (value.includes('.') || value.includes(',')) {
            e.preventDefault();
            return;
          }
          // Cho phép nhập dấu phẩy (sẽ được convert thành dấu chấm trong onChange)
          return;
        }

        // Ngăn các ký tự khác như -, +, và dấu chấm/phẩy khi là integer
        if (
          type === 'number' &&
          (e.key === '-' || e.key === '+' || (integer && (e.key === '.' || e.key === ',')))
        ) {
          e.preventDefault();
          return;
        }
        if (type === 'number' && e.key.match(/[^0-9]/i) && !FUNCTION_KEYS.includes(e.key)) {
          e.preventDefault();
          return;
        }
      },
      [currentValue, onEnter, maxLength, type, integer]
    );

    const transformNumberToText = useCallback(() => {
      const value = currentValue;
      if (!value) return '';
      try {
        return readNumber(value);
      } catch {
        return '';
      }
    }, [currentValue]);

    const predictions = useCallback(() => {
      const value = currentValue?.toString() || '';
      if (!value || value.length >= 11) return [];
      if (value.length < 9 && value.slice(4, value.length).match(/[^0]/)) return [];

      const predicts = [];

      for (let i = 11; i >= 9; i--) {
        if (value.length > i) break;

        predicts.push(value.padEnd(i, '0'));
      }

      return predicts.map(predict => [predict, formatNumberWithSpaces(predict, noGroup)]);
    }, [currentValue]);

    useEffect(() => {
      if (inputRef.current == null) return;
      inputRef.current.addEventListener('keydown', handleInput);

      return () => {
        inputRef.current?.removeEventListener('keydown', handleInput);
      };
    }, [handleInput]);

    const isNumber = type === 'number';
    const displayValue = isNumber
      ? formatNumberWithSpaces(currentValue ?? '', noGroup)
      : currentValue;
    const inputType = isNumber ? 'text' : type; // tránh xóa giá trị vì khoảng trắng không hợp lệ với input[number]
    const inputMode = isNumber ? (integer ? 'numeric' : 'decimal') : undefined;

    return (
      <div className={cn('flex flex-col gap-2', containerClassName)}>
        {label && (
          <div className="flex justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium w-full md:w-auto">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {isDirty && errors && errors.length > 0 && (
                <Tooltip content={errors[0]}>
                  <WarningIcon className="w-4 h-4 text-red-500" />
                </Tooltip>
              )}
            </div>

            {predict && (
              <div className="text-sm text-gray-700 shrink overflow-hidden">
                <ul className="flex gap-3 w-full">
                  {predictions().map(([num, text]) => (
                    <li
                      className="text-xs text-blue-700 overflow-hidden text-ellipsis whitespace-nowrap hover:underline cursor-pointer"
                      onClick={() => {
                        setCurrentValue(num);
                        onChange?.(num);
                      }}
                      key={num}
                      title={text}
                    >
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <div
          className={cn(
            'flex rounded-lg border-1 border-gray-300 rounded-lg transition-all h-10 overflow-hidden',
            'focus-within:ring-2 focus-within:ring-blue-500/20 hover:ring-blue-500/10',
            'focus-within:border-blue-500 hover:border-gray-300',
            isValid !== true && 'ring-2 ring-red-300',
            isDirty && errors && errors.length > 0 && 'ring-2 ring-red-300'
          )}
        >
          <input
            ref={inputRef}
            id={id}
            type={inputType as any}
            inputMode={inputMode as any}
            min={min}
            max={max}
            disabled={disabled}
            placeholder={placeholder}
            value={displayValue}
            className={cn(
              className,
              'w-full px-4 py-2 h-10 placeholder:text-gray-400 text-sm outline-none grow',
              disabled && 'text-gray-400 bg-gray-100 cursor-not-allowed'
            )}
            onChange={e => {
              setIsDirty(true);
              let newValue = e.target.value;
              if (type === 'number') {
                newValue = newValue.replace(/[^0-9]/g, '');
              }

              // Chỉ xóa khoảng trắng cho type number
              if (type === 'number') {
                newValue = newValue.replace(/\s/g, '');
              }

              // Convert dấu phẩy thành dấu chấm cho số thập phân
              if (type === 'number' && !integer) {
                newValue = newValue.replace(/,/g, '.');
                // Đảm bảo chỉ có một dấu chấm
                const parts = newValue.split('.');
                if (parts.length > 2) {
                  newValue = parts[0] + '.' + parts.slice(1).join('');
                }
              }

              // Xử lý trường hợp value = "0": thay thế bằng số mới khi gõ số
              if (type === 'number') {
                const prevValue = currentValue?.toString() || '';
                // Nếu giá trị trước là "0" (chính xác)
                if (prevValue === '0' && newValue.length > 1) {
                  // Nếu không có dấu chấm/phẩy (không phải đang nhập số thập phân)
                  if (!newValue.includes('.') && !newValue.includes(',')) {
                    // Nếu bắt đầu bằng "0" + số, bỏ "0" đi (thay thế "0" bằng số mới)
                    if (newValue.match(/^0\d/)) {
                      newValue = newValue.substring(1);
                    }
                  }
                  // Nếu có dấu chấm/phẩy ngay sau "0" (0. hoặc 0,), giữ nguyên
                }
              }

              setCurrentValue(newValue);
              onChange?.(newValue);
            }}
            {...props}
          />
          {units && (
            <>
              {typeof units === 'string' ? (
                <div
                  className={cn(
                    'px-3 py-2 h-10 text-sm text-gray-700 cursor-pointer select-none text-sm',
                    'border-l-0 rounded-l-none',
                    className,
                    disabled && 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  )}
                >
                  {units}
                </div>
              ) : (
                <Dropdown
                  align="right"
                  options={units as any}
                  onOptionClick={opt => onUnitChange?.(opt.value as any)}
                >
                  <div
                    className={cn(
                      'px-3 py-2 h-10 text-sm text-gray-700 cursor-pointer select-none text-sm',
                      'border-l-0 rounded-l-none',
                      disabled && 'text-gray-400 bg-gray-100 cursor-not-allowed'
                    )}
                  >
                    {(units as any).find((unit: any) => unit.value === unitValue)?.label}
                  </div>
                </Dropdown>
              )}
            </>
          )}
          {children && children}
        </div>
        {showTextNumber && (
          <span className="text-xs text-gray-700 italic">{transformNumberToText()}</span>
        )}
        {description && <span className="text-xs text-green-500 w-full">{description}</span>}
      </div>
    );
  }
);
